import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ScrollReveal from './ScrollReveal.jsx';

const CARD_STYLES = [
  {
    iconWrap: 'bg-violet-100 ring-violet-300/60',
    iconClass: 'text-[#7F22FE]',
    cta: 'bg-violet-600 text-white hover:bg-violet-700',
    cardHover: 'hover:border-violet-300/80 hover:shadow-[0_12px_40px_rgba(127,34,254,0.18)]',
  },
  {
    iconWrap: 'bg-green-100 ring-[#1acb12]/50',
    iconClass: 'text-[#00A63E]',
    cta: 'bg-[#0d2b7f] text-white hover:bg-[#0a2266]',
    cardHover: 'hover:border-[#1acb12]/70 hover:shadow-[0_12px_40px_rgba(26,203,18,0.2)]',
  },
  {
    iconWrap: 'bg-orange-100 ring-orange-300/70',
    iconClass: 'text-[#F54900]',
    cta: 'bg-[#F54900] text-white hover:bg-[#d93d00]',
    cardHover: 'hover:border-orange-300/80 hover:shadow-[0_12px_40px_rgba(245,73,0,0.18)]',
  },
  {
    iconWrap: 'bg-sky-100 ring-sky-300/70',
    iconClass: 'text-[#0284C7]',
    cta: 'bg-[#0284C7] text-white hover:bg-[#0369A1]',
    cardHover: 'hover:border-sky-300/80 hover:shadow-[0_12px_40px_rgba(2,132,199,0.18)]',
  },
];

const linkWrapClass =
  'group flex h-full min-h-0 flex-col items-center justify-center rounded-xl border border-slate-200/90 bg-white px-2.5 py-3 no-underline text-inherit shadow-sm outline-none transition duration-300 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:rounded-2xl sm:border-2 sm:px-3 sm:py-4 md:px-4 md:py-5 lg:px-5 lg:py-6';

function HighlightCardLink({ href, external, cardHover, children }) {
  const className = `${linkWrapClass} ${cardHover}`;

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  if (href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

export default function HighlightsGrid({ items }) {
  const count = items?.length ?? 0;
  const gridClass =
    count >= 4
      ? 'grid grid-cols-2 gap-2.5 sm:gap-3 md:gap-4 xl:grid-cols-4 xl:gap-5'
      : 'grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3 lg:gap-5';

  return (
    <section className="relative overflow-hidden border-y border-slate-200 bg-white py-6 sm:py-8 lg:py-12">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute left-1/2 top-0 size-[min(100%,520px)] -translate-x-1/2 rounded-full bg-[#FBFFE1]/70 blur-3xl md:blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className={gridClass}>
          {items.map((item, i) => {
            const style = CARD_STYLES[i % CARD_STYLES.length];
            const Icon = item.icon;
            return (
              <ScrollReveal key={item.title} delayMs={i * 50} className="min-w-0 h-full">
                <HighlightCardLink href={item.href} external={item.external} cardHover={style.cardHover}>
                  <div
                    className={`flex size-10 shrink-0 items-center justify-center rounded-full ring-2 shadow-md transition duration-300 group-hover:scale-105 group-hover:shadow-lg sm:size-12 md:size-14 lg:size-[4.75rem] ${style.iconWrap}`}
                  >
                    <Icon
                      className={`h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 lg:h-9 lg:w-9 ${style.iconClass}`}
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </div>
                  <div className="mt-2 space-y-1 text-center sm:mt-3 sm:space-y-1.5 md:mt-4 md:space-y-2">
                    <h3 className="font-display text-xs font-bold leading-snug text-[#0d2b7f] sm:text-sm md:text-base lg:text-lg">
                      {item.title}
                    </h3>
                    <p className="hidden text-sm leading-relaxed text-slate-600 lg:line-clamp-3 lg:block lg:text-base">
                      {item.text}
                    </p>
                    {item.buttonText ? (
                      <span
                        className={`mt-1 hidden max-w-full items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold shadow-md transition duration-300 group-hover:gap-2 lg:inline-flex ${style.cta}`}
                      >
                        <span className="truncate">{item.buttonText.replace(/\s*→\s*$/, '')}</span>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 transition duration-300 group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      </span>
                    ) : null}
                  </div>
                </HighlightCardLink>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
